import LinkIcon from "@mui/icons-material/Link";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildGroups,
  collectSources,
  diffWords,
  reasonLabel,
  ReasonTone,
  shortUrl,
} from "./utils";

export interface FactCheckFinding {
  id: number;
  section_heading: string;
  category:
    | "factual_error"
    | "harmful_to_client"
    | "link_issue"
    | "outdated_info"
    | "unverifiable_claim"
    | "agent_proposed"
    | string;
  severity: "critical" | "warning" | "suggestion" | string;
  original_text: string;
  replacement_text: string | null;
  reasoning: string;
  source: string;
  action: "replace" | "remove" | "flag_for_review" | string;
  applied?: boolean;
  status?: "pending" | "applied" | "declined";
  // Set on findings promoted from `factCheckAutoReview.proposedNewFindings`.
  // `agentProposedKey` is the dedup natural key (`${causedBy}|${original_text}`)
  // so reload-time merge skips entries the backend already persisted.
  agentProposedKey?: string;
  causedBy?: number;
  evidenceUrls?: string[];
  locator?: {
    start_char?: number | null;
    end_char?: number | null;
    context_before?: string | null;
    context_after?: string | null;
    match_strategy?: string;
  };
  // Captured when the finding is applied. Preserves the article-side state
  // separately from `original_text` (which is the audit record of what was
  // there before). Re-anchoring the green highlight on reload uses these.
  applied_text?: string;
  // Intentionally NO start_char/end_char here — positions become stale the
  // moment any other edit happens. Use context_before/after for re-anchoring.
  applied_locator?: {
    context_before?: string | null;
    context_after?: string | null;
    applied_at?: string;
  };
  // Written by the evidence-first fact-check (merged-v2). Read for the reason
  // label and for grouping a finding under the one it follows from.
  decision?: "apply" | "escalate" | string | null;
  escalation_reason?: string | null;
  confidence?: string | null;
  claim_type?: string | null;
  claim_id?: string | null;
  depends_on_finding_ids?: Array<number | string> | null;
  caused_by?: number | string | null;
  humanComment?: AutoReviewDecision["humanComment"];
}

export interface AutoReviewDecision {
  findingId: number;
  decision: "accept" | "reject" | "escalate" | string;
  finalReplacementText: string | null;
  overridesFactChecker: boolean;
  agentReasoning: string;
  evidenceUrls: string[];
  humanComment: {
    reason: string;
    conflictingSources?: Array<{ url?: string; claim?: string } | Record<string, string>>;
    suggestedOptions?: string[];
  } | null;
}

export interface ProposedNewFinding {
  causedBy: number;
  section_heading: string;
  original_text: string;
  replacement_text: string;
  reasoning: string;
  severity: "warning" | "suggestion" | string;
  action: "replace" | "remove" | string;
  evidenceUrls?: string[];
}

export interface AutoReview {
  status?: "queued" | "running" | "completed" | "failed" | string;
  completedAt?: string;
  decisions?: AutoReviewDecision[];
  proposedNewFindings?: ProposedNewFinding[];
  summary?: {
    accepted?: number;
    rejected?: number;
    escalated?: number;
    overrodeFactChecker?: number;
    newFindingsProposed?: number;
  };
}

interface FactCheckSidebarContentProps {
  findings: FactCheckFinding[];
  onSelectFinding?: (finding: FactCheckFinding) => void;
  activeFindingId?: number | null;
  onApplyAction?: (finding: FactCheckFinding) => void;
  onDeclineAction?: (finding: FactCheckFinding) => void;
  // Apply or decline several findings in one go (a grouped card).
  onApplyGroup?: (findings: FactCheckFinding[]) => void;
  onDeclineGroup?: (findings: FactCheckFinding[]) => void;
  // Finding id -> position in the article; cards follow this order.
  positions?: Record<number, number>;
  autoReview?: AutoReview | null;
  // True while a backend /article/apply-finding call is in flight. Disables
  // every apply/decline button so a second action can't queue a stale
  // /article/update behind the apply and clobber its result.
  isApplyInProgress?: boolean;
}

const severityColor = (severity: string) => {
  if (severity === "critical") return { bg: "#FEE2E2", text: "#B91C1C" };
  if (severity === "warning") return { bg: "#FEF3C7", text: "#B45309" };
  return { bg: "#E0E7FF", text: "#3730A3" };
};

const toneColor: Record<ReasonTone, { bg: string; text: string }> = {
  escalate: { bg: "#FEF3C7", text: "#92400E" },
  confirmed: { bg: "#D1FAE5", text: "#065F46" },
  neutral: { bg: "#F3F4F6", text: "#374151" },
};

type AgentVerdict = "accept" | "override" | "reject" | "escalate" | "unknown";

const classifyDecision = (d?: AutoReviewDecision | null): AgentVerdict => {
  if (!d) return "unknown";
  if (d.decision === "accept") return d.overridesFactChecker ? "override" : "accept";
  if (d.decision === "reject") return "reject";
  if (d.decision === "escalate") return "escalate";
  return "unknown";
};

// Only verdicts that change what the editor should do get a chip; accept and
// escalate are already said by the reason label.
const verdictChips: Partial<Record<AgentVerdict, { chipBg: string; text: string; label: string; Icon: React.ElementType }>> = {
  override: { chipBg: "#EDE9FE", text: "#5B21B6", label: "Agent suggests other wording", Icon: EditOutlinedIcon },
  reject:   { chipBg: "#FEE2E2", text: "#991B1B", label: "Agent: original looks correct", Icon: HighlightOffIcon },
};

const effectiveStatus = (f: FactCheckFinding) => f.status || (f.applied ? "applied" : "pending");

const FactCheckSidebarContent: React.FC<FactCheckSidebarContentProps> = ({
  findings = [],
  onSelectFinding,
  activeFindingId,
  onApplyAction,
  onDeclineAction,
  onApplyGroup,
  onDeclineGroup,
  positions,
  autoReview,
  isApplyInProgress = false,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Scroll to the active card whenever the active finding changes (e.g. user clicked underlined text)
  useEffect(() => {
    if (activeFindingId == null) return;
    const el = cardRefs.current[String(activeFindingId)];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeFindingId]);

  const decisionsById = useMemo<Record<number, AutoReviewDecision>>(() => {
    const map: Record<number, AutoReviewDecision> = {};
    (autoReview?.decisions || []).forEach((d) => {
      const id = Number(d?.findingId);
      if (Number.isFinite(id)) map[id] = d;
    });
    return map;
  }, [autoReview]);

  // Agent-proposed findings always render in their own section regardless of
  // status — applied/declined cards stay there and never bleed into the main list.
  const proposedFindings = useMemo(
    () => buildGroups(findings.filter((f) => f.category === "agent_proposed"), positions)
      .flatMap((g) => [g.main, ...g.related]),
    [findings, positions],
  );
  const groups = useMemo(
    () => buildGroups(findings.filter((f) => f.category !== "agent_proposed"), positions),
    [findings, positions],
  );

  const pendingCount = findings.filter((f) => effectiveStatus(f) === "pending").length;

  const handleCopyText = async (
    event: React.MouseEvent,
    text: string,
    key: string
  ) => {
    event.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((prev) => (prev === key ? null : prev));
      }, 1500);
    } catch (error) {
      console.error("Failed to copy text", error);
    }
  };

  const renderReasoningWithLinks = (text: string) => {
    const parts = text.split(/(finding #\d+)/gi);
    return parts.map((part, idx) => {
      const match = part.match(/^finding #(\d+)$/i);
      if (match) {
        const refId = parseInt(match[1], 10);
        return (
          <Link
            key={idx}
            component="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              const el = cardRefs.current[String(refId)];
              el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
              const target = findings.find((f) => f.id === refId);
              if (target) onSelectFinding?.(target);
            }}
            sx={{ fontSize: "inherit", color: "#6366F1", fontWeight: 600, cursor: "pointer", verticalAlign: "baseline" }}
          >
            {part}
          </Link>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const copyButton = (title: string, text: string, key: string) => (
    <Tooltip title={title}>
      <IconButton
        size="small"
        data-disable-card-select="true"
        onClick={(event) => handleCopyText(event, text, key)}
        sx={{ p: "2px", flexShrink: 0 }}
      >
        {copiedKey === key ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#16A34A" }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    </Tooltip>
  );

  // Original and replacement in one block, removed words struck through in
  // red and added words in green.
  const renderChange = (original: string, replacement: string | null, isRemove: boolean) => {
    const rep = (replacement || "").trim();
    const parts = isRemove
      ? [{ type: "del" as const, text: original }]
      : rep
        ? diffWords(original, replacement as string)
        : [{ type: "same" as const, text: original }];
    // A full rewrite reads better as the old line above the new one.
    const isRewrite =
      parts.length === 2 && parts[0].type === "del" && parts[1].type === "ins" && parts[0].text === original;
    return (
      <Box data-disable-card-select="true" sx={{ fontSize: "13px", color: "#111827", lineHeight: 1.55, userSelect: "text", wordBreak: "break-word" }}>
        {parts.map((p, idx) =>
          p.type === "del" ? (
            <Box key={idx} component="del" sx={{ backgroundColor: "#FEE2E2", color: "#991B1B", borderRadius: "2px", ...(isRewrite && { display: "block", mb: 0.5 }) }}>{p.text}</Box>
          ) : p.type === "ins" ? (
            <Box key={idx} component="ins" sx={{ backgroundColor: "#D1FAE5", color: "#065F46", textDecoration: "none", borderRadius: "2px", ...(isRewrite && { display: "block" }) }}>{p.text}</Box>
          ) : (
            <span key={idx}>{p.text}</span>
          )
        )}
        {!isRemove && !rep && (
          <Typography component="span" sx={{ display: "block", fontSize: "12px", color: "#6B7280", mt: 0.5 }}>
            No change suggested. Check the text and mark it reviewed.
          </Typography>
        )}
      </Box>
    );
  };

  const renderSources = (finding: FactCheckFinding, decision?: AutoReviewDecision) => {
    const sources = collectSources(finding.source, [
      ...(decision?.evidenceUrls || []),
      ...(finding.evidenceUrls || []),
    ]);
    if (sources.length === 0) return null;
    return (
      <Box data-disable-card-select="true" sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {sources.map((part, idx) => {
          const isUrl = /^https?:\/\//i.test(part);
          return (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <LinkIcon sx={{ fontSize: 14, color: "#6366F1", flexShrink: 0 }} />
              {isUrl ? (
                <Tooltip title={part}>
                  <Link href={part} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "12px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {shortUrl(part)}
                  </Link>
                </Tooltip>
              ) : (
                <Typography sx={{ fontSize: "12px", color: "#4B5563", wordBreak: "break-word", minWidth: 0 }}>{part}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderReasoning = (finding: FactCheckFinding, decision?: AutoReviewDecision) => {
    const text =
      finding.reasoning ||
      decision?.humanComment?.reason ||
      finding.humanComment?.reason ||
      "";
    // Legacy auto-review runs had their own reasoning; merged-v2 copies the
    // fact-checker's, so only show it when it says something new.
    const agentText =
      decision?.agentReasoning && decision.agentReasoning.trim() !== text.trim()
        ? decision.agentReasoning
        : "";
    if (!text && !agentText) return null;
    return (
      <Box data-disable-card-select="true" sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {text && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
            <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: "#6B7280", mt: "2px", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "12.5px", color: "#374151", userSelect: "text", flex: 1, minWidth: 0, wordBreak: "break-word" }}>
              {renderReasoningWithLinks(text)}
            </Typography>
            {copyButton("Copy reasoning", text, `reasoning-${finding.id}`)}
          </Box>
        )}
        {agentText && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 16, color: "#6366F1", mt: "2px", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "12.5px", color: "#374151", userSelect: "text", flex: 1, minWidth: 0, wordBreak: "break-word" }}>
              {agentText}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const findingHeader = (finding: FactCheckFinding, decision: AutoReviewDecision | undefined, extra?: React.ReactNode) => {
    const label = reasonLabel(finding, decision);
    const tone = toneColor[label.tone];
    const verdict = verdictChips[classifyDecision(decision)];
    const VIcon = verdict?.Icon;
    const status = effectiveStatus(finding);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>#{finding.id}</Typography>
        {finding.severity === "critical" && (
          <Chip label="Critical" size="small" sx={{ backgroundColor: "#FEE2E2", color: "#B91C1C", fontWeight: 700 }} />
        )}
        <Chip label={label.text} size="small" sx={{ backgroundColor: tone.bg, color: tone.text, fontWeight: 700 }} />
        {verdict && (
          <Chip
            icon={VIcon ? <VIcon sx={{ fontSize: 14, color: `${verdict.text} !important` }} /> : undefined}
            label={verdict.label}
            size="small"
            sx={{ backgroundColor: verdict.chipBg, color: verdict.text, fontWeight: 700 }}
          />
        )}
        {status === "applied" && (
          <Chip label="Applied" size="small" sx={{ backgroundColor: "#22C55E", color: "#FFFFFF", fontWeight: 700 }} />
        )}
        {status === "declined" && (
          <Chip label="Declined" size="small" sx={{ backgroundColor: "#E5E7EB", color: "#4B5563", fontWeight: 700 }} />
        )}
        {extra}
        <Typography sx={{ fontSize: "11.5px", color: "#9CA3AF", ml: "auto", maxWidth: "45%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {finding.section_heading || "Introduction"}
        </Typography>
      </Box>
    );
  };

  const actionInfo = (finding: FactCheckFinding) => {
    const normalizedAction = String(finding.action || "").trim().toLowerCase();
    const isRemoveAction = normalizedAction === "remove";
    const hasReplacement = !!finding.replacement_text?.trim();
    const isReplaceLike =
      normalizedAction === "replace" || (normalizedAction === "flag_for_review" && hasReplacement);
    const applyLabel = isReplaceLike ? "Apply" : isRemoveAction ? "Remove text" : "Mark reviewed";
    // A replace with no replacement text has nothing to apply.
    const applyBlocked = normalizedAction === "replace" && !hasReplacement;
    return { isRemoveAction, applyLabel, applyBlocked };
  };

  const buttonSx = { textTransform: "none", fontWeight: 600, minWidth: "unset", px: 1.5, py: 0.5 } as const;

  // Apply / Decline for one finding. `compact` is the small per-finding
  // version used inside a grouped card.
  const renderButtons = (finding: FactCheckFinding, decision: AutoReviewDecision | undefined, compact = false) => {
    const status = effectiveStatus(finding);
    const isApplied = status === "applied";
    const isDeclined = status === "declined";
    const isResolved = isApplied || isDeclined;
    if (compact && isResolved) return null;
    const verdict = classifyDecision(decision);
    const { applyLabel, applyBlocked } = actionInfo(finding);
    const recommendApply = verdict === "accept" || verdict === "override";
    const recommendDecline = verdict === "reject";
    const variant = (strong: boolean) => (compact ? "text" : strong && !isResolved ? "contained" : "outlined");
    return (
      <Box data-disable-card-select="true" sx={{ display: "flex", gap: compact ? 0.5 : 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant={variant(recommendApply)}
          onClick={(event) => {
            event.stopPropagation();
            onApplyAction?.(finding);
          }}
          disabled={isResolved || isApplyInProgress || !onApplyAction || applyBlocked}
          sx={{
            ...buttonSx,
            ...(compact && { px: 1, py: 0.25, fontSize: "12px" }),
            ...(isApplied && {
              backgroundColor: "#22C55E",
              "&.Mui-disabled": { backgroundColor: "#22C55E", color: "#FFFFFF", opacity: 1 },
            }),
          }}
        >
          {isApplied ? "Applied" : applyLabel}
        </Button>

        {/* Override: apply agent's preferred wording */}
        {verdict === "override" && decision?.finalReplacementText && !isResolved && (
          <Button
            size="small"
            variant={compact ? "text" : "contained"}
            onClick={(event) => {
              event.stopPropagation();
              onApplyAction?.({ ...finding, replacement_text: decision.finalReplacementText! });
            }}
            disabled={!onApplyAction || isApplyInProgress}
            sx={{
              ...buttonSx,
              ...(compact
                ? { px: 1, py: 0.25, fontSize: "12px", color: "#7C3AED" }
                : { backgroundColor: "#8B5CF6", "&:hover": { backgroundColor: "#7C3AED" } }),
            }}
          >
            Apply agent's wording
          </Button>
        )}

        <Button
          size="small"
          variant={variant(recommendDecline)}
          color={recommendDecline && !isResolved ? "error" : "primary"}
          onClick={(event) => {
            event.stopPropagation();
            onDeclineAction?.(finding);
          }}
          disabled={isResolved || isApplyInProgress || !onDeclineAction}
          sx={{
            ...buttonSx,
            ...(compact && { px: 1, py: 0.25, fontSize: "12px" }),
            ...(isDeclined && {
              borderColor: "#9CA3AF",
              color: "#6B7280",
              "&.Mui-disabled": { borderColor: "#9CA3AF", color: "#6B7280", opacity: 1 },
            }),
          }}
        >
          {isDeclined ? "Declined" : "Decline"}
        </Button>
      </Box>
    );
  };

  // The change, its copy buttons and (for an agent override) the agent's wording.
  const renderChangeBlock = (finding: FactCheckFinding, decision: AutoReviewDecision | undefined) => {
    const { isRemoveAction } = actionInfo(finding);
    const verdict = classifyDecision(decision);
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {renderChange(finding.original_text, finding.replacement_text, isRemoveAction)}
          </Box>
          <Box data-disable-card-select="true" sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
            {copyButton("Copy original text", finding.original_text, `original-${finding.id}`)}
            {!!finding.replacement_text?.trim() &&
              copyButton("Copy suggested text", finding.replacement_text || "", `replacement-${finding.id}`)}
          </Box>
        </Box>
        {verdict === "override" && decision?.finalReplacementText && (
          <Box data-disable-card-select="true" sx={{ border: "1px dashed #C4B5FD", borderRadius: 1, p: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#5B21B6", flex: 1 }}>
                Agent's wording
              </Typography>
              {copyButton("Copy agent wording", decision.finalReplacementText, `agent-preferred-${finding.id}`)}
            </Box>
            {renderChange(finding.original_text, decision.finalReplacementText, false)}
          </Box>
        )}
      </>
    );
  };

  const cardSx = (isActive: boolean, dim: boolean) => ({
    p: 2,
    mb: 2,
    border: isActive ? "2px solid #6366F1" : "1px solid #E5E7EB",
    boxShadow: "none",
    overflow: "hidden",
    cursor: onSelectFinding ? "pointer" : "default",
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
    ...(dim && { opacity: 0.55 }),
    "&:hover": onSelectFinding
      ? { borderColor: "#6366F1", boxShadow: isActive ? "none" : "0 0 0 1px #6366F1 inset" }
      : {},
  });

  const selectOnClick = (finding: FactCheckFinding) => (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-disable-card-select="true"]')) return;
    onSelectFinding?.(finding);
  };

  const renderSingleCard = (finding: FactCheckFinding) => {
    const decision = decisionsById[finding.id];
    return (
      <Card
        key={`${finding.id}-${finding.original_text.slice(0, 24)}`}
        ref={(el) => { cardRefs.current[String(finding.id)] = el as HTMLDivElement | null; }}
        onClick={selectOnClick(finding)}
        sx={cardSx(activeFindingId === finding.id, effectiveStatus(finding) === "declined")}
      >
        {findingHeader(finding, decision)}
        {renderChangeBlock(finding, decision)}
        {renderButtons(finding, decision)}
        {renderReasoning(finding, decision)}
        {renderSources(finding, decision)}
      </Card>
    );
  };

  // A main finding with the findings that follow from it (or that touch the
  // same sentence or claim): one card, one Apply all / Decline all, and small
  // per-finding buttons for the rare case where they need different answers.
  const renderGroupCard = (main: FactCheckFinding, related: FactCheckFinding[]) => {
    const members = [main, ...related];
    const pending = members.filter((f) => effectiveStatus(f) === "pending");
    const applicable = pending.filter((f) => !actionInfo(f).applyBlocked);
    const isActive = members.some((f) => f.id === activeFindingId);
    return (
      <Card
        key={`group-${main.id}`}
        ref={(el) => {
          members.forEach((f) => { cardRefs.current[String(f.id)] = el as HTMLDivElement | null; });
        }}
        onClick={selectOnClick(main)}
        sx={cardSx(isActive, pending.length === 0 && members.every((f) => effectiveStatus(f) === "declined"))}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#4338CA", letterSpacing: "0.02em" }}>
            {members.length} RELATED FINDINGS
          </Typography>
          {pending.length > 0 && pending.length < members.length && (
            <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{pending.length} left</Typography>
          )}
        </Box>

        {pending.length > 0 && (
          <Box data-disable-card-select="true" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="contained"
              onClick={(event) => {
                event.stopPropagation();
                onApplyGroup?.(applicable);
              }}
              disabled={isApplyInProgress || !onApplyGroup || applicable.length === 0}
              sx={buttonSx}
            >
              Apply all ({applicable.length})
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onDeclineGroup?.(pending);
              }}
              disabled={isApplyInProgress || !onDeclineGroup}
              sx={buttonSx}
            >
              Decline all ({pending.length})
            </Button>
          </Box>
        )}

        {members.map((finding, idx) => {
          const decision = decisionsById[finding.id];
          return (
            <Box
              key={`${finding.id}-${finding.original_text.slice(0, 24)}`}
              onClick={(event) => {
                event.stopPropagation();
                selectOnClick(finding)(event);
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                ...(idx > 0 && { borderTop: "1px solid #E5E7EB", pt: 1.25 }),
                ...(effectiveStatus(finding) === "declined" && { opacity: 0.55 }),
                ...(finding.id === activeFindingId && related.length > 0 && {
                  borderLeft: "3px solid #6366F1",
                  pl: 1,
                }),
              }}
            >
              {findingHeader(finding, decision)}
              {renderChangeBlock(finding, decision)}
              {renderButtons(finding, decision, true)}
              {renderReasoning(finding, decision)}
              {renderSources(finding, decision)}
            </Box>
          );
        })}
      </Card>
    );
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "16px", fontFamily: "Inter", color: "#121828", mb: 2 }}>
        {findings.length === 0
          ? "No findings"
          : `${findings.length} finding${findings.length === 1 ? "" : "s"} · ${pendingCount} left to review`}
      </Typography>

      {groups.length === 0 && proposedFindings.length === 0 ? (
        <Card sx={{ p: 2, border: "1px solid #E5E7EB", boxShadow: "none" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TaskAltIcon sx={{ color: "#16A34A" }} />
            <Typography sx={{ fontSize: "14px", color: "#111827" }}>No fact-check findings for this article.</Typography>
          </Box>
        </Card>
      ) : (
        groups.map(({ main, related }) =>
          related.length === 0 ? renderSingleCard(main) : renderGroupCard(main, related)
        )
      )}

      {proposedFindings.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 18, color: "#5B21B6" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#5B21B6" }}>
              Agent-Proposed Findings
            </Typography>
            <Chip
              label={proposedFindings.filter((f) => (f.status ?? "pending") === "pending").length}
              size="small"
              sx={{ backgroundColor: "#EDE9FE", color: "#5B21B6", fontWeight: 700, height: 20 }}
            />
          </Box>
          <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 1.5 }}>
            New issues the agent flagged while reviewing other findings.
          </Typography>

          {proposedFindings.map((p) => {
            const sev = severityColor(p.severity);
            const pStatus = p.status ?? "pending";
            const pIsApplied = pStatus === "applied";
            const pIsDeclined = pStatus === "declined";
            return (
              <Card
                key={`proposed-${p.id}`}
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('[data-disable-card-select="true"]')) return;
                  onSelectFinding?.(p);
                }}
                sx={{
                  p: 2,
                  mb: 2,
                  border: "1px dashed #8B5CF6",
                  backgroundColor: "#FBFAFF",
                  boxShadow: "none",
                  cursor: onSelectFinding ? "pointer" : "default",
                  "&:hover": onSelectFinding
                    ? { borderColor: "#7C3AED", boxShadow: "0 0 0 1px #7C3AED inset" }
                    : {},
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      icon={<SmartToyOutlinedIcon sx={{ fontSize: 14, color: "#5B21B6 !important" }} />}
                      label="AGENT-PROPOSED"
                      size="small"
                      sx={{ backgroundColor: "#EDE9FE", color: "#5B21B6", fontWeight: 700 }}
                    />
                    <Chip label={p.severity.toUpperCase()} size="small" sx={{ backgroundColor: sev.bg, color: sev.text, fontWeight: 700 }} />
                  </Box>
                  <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>
                    From #{p.causedBy}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>
                  Section: {p.section_heading || "Introduction"}
                </Typography>

                <Box
                  data-disable-card-select="true"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
                >
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>Original</Typography>
                  <Tooltip title="Copy original text">
                    <IconButton
                      size="small"
                      onClick={(event) => handleCopyText(event, p.original_text, `original-proposed-${p.id}`)}
                      sx={{ p: "2px" }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {copiedKey === `original-proposed-${p.id}` && (
                    <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600 }}>Copied</Typography>
                  )}
                </Box>
                <Typography data-disable-card-select="true" sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text", wordBreak: "break-word" }}>
                  {p.original_text}
                </Typography>

                {p.action !== "remove" && p.replacement_text && (
                  <>
                    <Box
                      data-disable-card-select="true"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
                    >
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                        Suggested Replacement
                      </Typography>
                      <Tooltip title="Copy replacement text">
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleCopyText(event, p.replacement_text || "", `replacement-proposed-${p.id}`)
                          }
                          sx={{ p: "2px" }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      {copiedKey === `replacement-proposed-${p.id}` && (
                        <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600 }}>Copied</Typography>
                      )}
                    </Box>
                    <Typography data-disable-card-select="true" sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text", wordBreak: "break-word" }}>
                      {p.replacement_text}
                    </Typography>
                  </>
                )}

                <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(event) => {
                      event.stopPropagation();
                      onApplyAction?.(p);
                    }}
                    disabled={pIsApplied || pIsDeclined || !onApplyAction || isApplyInProgress || (p.action === "replace" && !p.replacement_text?.trim())}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: "unset",
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: pIsApplied ? "#22C55E" : "#8B5CF6",
                      "&:hover": { backgroundColor: pIsApplied ? "#22C55E" : "#7C3AED" },
                      ...(pIsApplied && {
                        "&.Mui-disabled": { backgroundColor: "#22C55E", color: "#FFFFFF", opacity: 1 },
                      }),
                    }}
                  >
                    {pIsApplied ? "Applied" : p.action === "remove" ? "Apply Remove" : "Apply Replace"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeclineAction?.(p);
                    }}
                    disabled={pIsApplied || pIsDeclined || !onDeclineAction || isApplyInProgress}
                    sx={{ textTransform: "none", fontWeight: 600, minWidth: "unset", px: 1.5, py: 0.5 }}
                  >
                    {pIsDeclined ? "Declined" : "Decline"}
                  </Button>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: "#6B7280", mt: "2px", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "12px", color: "#4B5563", userSelect: "text", flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                    {p.reasoning}
                  </Typography>
                </Box>

                {Array.isArray(p.evidenceUrls) && p.evidenceUrls.length > 0 && (
                  <Box sx={{ mt: 0.75 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#5B21B6", mb: 0.25 }}>
                      Agent evidence:
                    </Typography>
                    {p.evidenceUrls.map((url, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <LinkIcon sx={{ fontSize: 12, color: "#8B5CF6", flexShrink: 0 }} />
                        <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "11px", wordBreak: "break-all" }}>
                          {url}
                        </Link>
                      </Box>
                    ))}
                  </Box>
                )}
                {(!Array.isArray(p.evidenceUrls) || p.evidenceUrls.length === 0) && (() => {
                  const rawMatches = (p.reasoning || "").match(
                    /https?:\/\/[^\s()[\]"'<>]+|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}\/[^\s()[\]"'<>]*/g
                  ) || [];
                  const urls = Array.from(
                    new Set(rawMatches.map((u) => (u.startsWith("http") ? u : `https://${u}`)))
                  );
                  if (urls.length === 0) return null;
                  return (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                      {urls.map((url: string, idx: number) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <LinkIcon sx={{ fontSize: 14, color: "#6366F1", flexShrink: 0 }} />
                          <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "12px", wordBreak: "break-all", minWidth: 0 }}>
                            {url}
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FactCheckSidebarContent;
